import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Upload, Trash2, Megaphone, Phone, Save, X, Check } from "lucide-react";
import { useCategories } from "@/hooks/useCategories";
const ALLOWED_IMAGE_TYPES = ["image/jpeg", "image/jpg", "image/png", "image/webp", "image/gif"];
const MAX_IMAGE_SIZE = 5 * 1024 * 1024; // 5MB

export const validateImageFile = (file: File, isRTL: boolean): string | null => {
  if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
    return isRTL
      ? "نوع الصورة غير مدعوم. الأنواع المسموح بها: JPG, PNG, WEBP, GIF"
      : "Unsupported image type. Allowed: JPG, PNG, WEBP, GIF";
  }
  if (file.size > MAX_IMAGE_SIZE) {
    return isRTL
      ? `حجم الصورة يجب أن يكون أقل من 5 ميجابايت (الحالي: ${(file.size / 1024 / 1024).toFixed(2)}MB)`
      : `Image must be less than 5MB (current: ${(file.size / 1024 / 1024).toFixed(2)}MB)`;
  }
  return null;
};


interface BannerAd {
  id: string;
  category_slug: string;
  position: number;
  image_url: string | null;
  link_url: string | null;
  start_date: string | null;
  end_date: string | null;
  is_active: boolean;
}

const toLocalInput = (iso: string | null) =>
  iso ? new Date(iso).toISOString().slice(0, 16) : "";

export default function SectionBannerAdsAdmin() {
  const { i18n } = useTranslation();
  const isRTL = i18n.language === "ar";
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { categories } = useCategories();

  const [whatsapp, setWhatsapp] = useState("");

  const { data: whatsappSetting } = useQuery({
    queryKey: ["banner_ads_whatsapp"],
    queryFn: async () => {
      const { data } = await supabase
        .from("site_settings")
        .select("setting_value")
        .eq("setting_key", "banner_ads_whatsapp")
        .maybeSingle();
      const val = (data?.setting_value as any) ?? { phone: "" };
      setWhatsapp(val.phone || "");
      return val;
    },
  });

  const saveWhatsapp = useMutation({
    mutationFn: async () => {
      // try update first
      const { data: existing } = await supabase
        .from("site_settings")
        .select("id")
        .eq("setting_key", "banner_ads_whatsapp")
        .maybeSingle();
      if (existing) {
        const { error } = await supabase
          .from("site_settings")
          .update({ setting_value: { phone: whatsapp } as any, updated_at: new Date().toISOString() })
          .eq("setting_key", "banner_ads_whatsapp");
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from("site_settings")
          .insert({ setting_key: "banner_ads_whatsapp", setting_value: { phone: whatsapp } as any });
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["banner_ads_whatsapp"] });
      toast.success(isRTL ? "تم حفظ رقم الواتساب" : "WhatsApp number saved");
    },
    onError: () => toast.error(isRTL ? "فشل الحفظ" : "Save failed"),
  });

  const { data: ads = [] } = useQuery({
    queryKey: ["all-section-banner-ads"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("section_banner_ads" as any)
        .select("*");
      if (error) throw error;
      return (data as unknown as BannerAd[]) || [];
    },
  });

  const upsertAd = useMutation({
    mutationFn: async (ad: Partial<BannerAd> & { category_slug: string; position: number }) => {
      const existing = ads.find((a) => a.category_slug === ad.category_slug && a.position === ad.position);
      if (existing) {
        const { error } = await supabase
          .from("section_banner_ads" as any)
          .update({
            image_url: ad.image_url ?? existing.image_url,
            link_url: ad.link_url ?? existing.link_url,
            start_date: ad.start_date ?? existing.start_date,
            end_date: ad.end_date ?? existing.end_date,
            is_active: ad.is_active ?? existing.is_active,
          })
          .eq("id", existing.id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from("section_banner_ads" as any).insert({
          category_slug: ad.category_slug,
          position: ad.position,
          image_url: ad.image_url ?? null,
          link_url: ad.link_url ?? null,
          start_date: ad.start_date ?? null,
          end_date: ad.end_date ?? null,
          is_active: ad.is_active ?? true,
        });
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["all-section-banner-ads"] });
      queryClient.invalidateQueries({ queryKey: ["section-banner-ads"] });
    },
  });

  const deleteImage = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("section_banner_ads" as any).update({ image_url: null }).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["all-section-banner-ads"] });
      queryClient.invalidateQueries({ queryKey: ["section-banner-ads"] });
      toast.success(isRTL ? "تم حذف الصورة" : "Image removed");
    },
  });

  const handleUpload = async (file: File, categorySlug: string, position: number) => {
    const err = validateImageFile(file, isRTL);
    if (err) {
      toast.error(err);
      return;
    }
    const fileName = `${categorySlug}-${position}-${Date.now()}.${file.name.split(".").pop()}`;
    const { error: upErr } = await supabase.storage.from("ad-images").upload(`section-banners/${fileName}`, file);
    if (upErr) {
      toast.error(isRTL ? "فشل الرفع" : "Upload failed");
      return;
    }
    const { data: urlData } = supabase.storage.from("ad-images").getPublicUrl(`section-banners/${fileName}`);
    await upsertAd.mutateAsync({ category_slug: categorySlug, position, image_url: urlData.publicUrl });
    toast.success(isRTL ? "تم رفع الصورة" : "Uploaded");
  };

  const visibleCats = categories.filter((c) => c.is_visible);

  return (
    <div className="space-y-6 p-4 md:p-6" dir={isRTL ? "rtl" : "ltr"}>
      <Button variant="ghost" onClick={() => navigate("/admin-dashboard")} className="flex items-center gap-2">
        {isRTL ? <ArrowRight className="w-4 h-4" /> : <ArrowLeft className="w-4 h-4" />}
        {isRTL ? "العودة للوحة التحكم" : "Back to Dashboard"}
      </Button>

      <div>
        <h1 className="text-2xl md:text-3xl font-bold flex items-center gap-3">
          <Megaphone className="w-7 h-7 text-primary" />
          {isRTL ? "البنرات الإعلانية بين الأقسام" : "Section Banner Ads"}
        </h1>
        <p className="text-muted-foreground mt-1 text-sm">
          {isRTL
            ? "3 بنرات فوق كل قسم. الفارغة تظهر كمساحة للتأجير عبر الواتساب"
            : "3 banners above each section. Empty slots show as rental spaces with WhatsApp contact"}
        </p>
        <div className="mt-3 inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-primary/10 border border-primary/30 text-xs sm:text-sm">
          <span className="font-bold text-primary">
            {isRTL ? "📐 المقاس الموصى به:" : "📐 Recommended size:"}
          </span>
          <span className="font-mono">1280 × 720 px</span>
          <span className="text-muted-foreground">
            {isRTL ? "(نسبة 16:9 — JPG/PNG/WEBP — حتى 5MB)" : "(ratio 16:9 — JPG/PNG/WEBP — up to 5MB)"}
          </span>
        </div>
      </div>

      {/* WhatsApp Setting */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Phone className="w-5 h-5 text-primary" />
            {isRTL ? "رقم الواتساب للتأجير" : "WhatsApp Number for Rentals"}
          </CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col sm:flex-row gap-3 items-end">
          <div className="flex-1 w-full">
            <Label className="mb-1 block">{isRTL ? "الرقم بصيغة دولية (مثال: 9665XXXXXXXX)" : "International format (e.g. 9665XXXXXXXX)"}</Label>
            <Input value={whatsapp} onChange={(e) => setWhatsapp(e.target.value)} placeholder="9665XXXXXXXX" />
          </div>
          <Button onClick={() => saveWhatsapp.mutate()} disabled={saveWhatsapp.isPending}>
            <Save className="w-4 h-4 mr-2" />
            {isRTL ? "حفظ" : "Save"}
          </Button>
        </CardContent>
      </Card>

      {/* Per-category banners */}
      <div className="space-y-6">
        {visibleCats.map((cat) => {
          const slug = cat.category_slug || cat.path.replace("/", "").replace("category/", "");
          return (
            <Card key={cat.id}>
              <CardHeader>
                <CardTitle className="text-base">
                  {isRTL ? cat.name_ar : cat.name_en}
                  <span className="text-xs text-muted-foreground mx-2">({slug})</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {[1, 2, 3].map((position) => {
                    const ad = ads.find((a) => a.category_slug === slug && a.position === position);
                    return (
                      <BannerSlot
                        key={position}
                        slug={slug}
                        position={position}
                        ad={ad}
                        isRTL={isRTL}
                        onUpload={(f) => handleUpload(f, slug, position)}
                        onUpdate={(patch) => upsertAd.mutate({ category_slug: slug, position, ...patch })}
                        onRemoveImage={() => ad && deleteImage.mutate(ad.id)}
                      />
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}

function BannerSlot({
  slug,
  position,
  ad,
  isRTL,
  onUpload,
  onUpdate,
  onRemoveImage,
}: {
  slug: string;
  position: number;
  ad: BannerAd | undefined;
  isRTL: boolean;
  onUpload: (f: File) => void;
  onUpdate: (patch: Partial<BannerAd>) => void;
  onRemoveImage: () => void;
}) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [linkUrl, setLinkUrl] = useState(ad?.link_url || "");
  const [startDate, setStartDate] = useState(toLocalInput(ad?.start_date || null));
  const [endDate, setEndDate] = useState(toLocalInput(ad?.end_date || null));
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);

  const handleFileSelect = (file: File) => {
    const err = validateImageFile(file, isRTL);
    if (err) {
      toast.error(err);
      if (fileRef.current) fileRef.current.value = "";
      return;
    }
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPendingFile(file);
    setPreviewUrl(URL.createObjectURL(file));
  };

  const cancelPreview = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPendingFile(null);
    setPreviewUrl(null);
    if (fileRef.current) fileRef.current.value = "";
  };

  const confirmUpload = async () => {
    if (!pendingFile) return;
    setUploading(true);
    try {
      await onUpload(pendingFile);
      cancelPreview();
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="border rounded-lg p-3 space-y-3 bg-muted/20">
      <div className="flex items-center justify-between">
        <span className="text-sm font-semibold">
          {isRTL ? `بنر ${position}` : `Banner ${position}`}
        </span>
        <div className="flex items-center gap-2">
          <span className="text-xs">{isRTL ? "نشط" : "Active"}</span>
          <Switch
            checked={ad?.is_active ?? true}
            onCheckedChange={(v) => onUpdate({ is_active: v })}
          />
        </div>
      </div>

      <input
        ref={fileRef}
        type="file"
        accept="image/jpeg,image/jpg,image/png,image/webp,image/gif"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) handleFileSelect(f);
        }}
      />

      {previewUrl ? (
        <div className="space-y-2">
          <div className="relative">
            <img src={previewUrl} alt="preview" className="w-full h-24 object-cover rounded ring-2 ring-primary" />
            <span className="absolute top-1 left-1 bg-primary text-primary-foreground text-[10px] px-2 py-0.5 rounded">
              {isRTL ? "معاينة" : "Preview"}
            </span>
          </div>
          <p className="text-[10px] text-muted-foreground truncate">
            {pendingFile?.name} ({((pendingFile?.size || 0) / 1024).toFixed(0)} KB)
          </p>
          <div className="flex gap-2">
            <Button size="sm" className="flex-1 h-8 text-xs" onClick={confirmUpload} disabled={uploading}>
              <Check className="h-3 w-3 mr-1" />
              {uploading ? (isRTL ? "جاري الحفظ..." : "Saving...") : (isRTL ? "حفظ" : "Save")}
            </Button>
            <Button size="sm" variant="outline" className="flex-1 h-8 text-xs" onClick={cancelPreview} disabled={uploading}>
              <X className="h-3 w-3 mr-1" />
              {isRTL ? "إلغاء" : "Cancel"}
            </Button>
          </div>
        </div>
      ) : ad?.image_url ? (
        <div className="relative">
          <img src={ad.image_url} alt="" className="w-full h-24 object-cover rounded" />
          <div className="absolute top-1 right-1 flex gap-1">
            <Button size="icon" variant="secondary" className="h-7 w-7" onClick={() => fileRef.current?.click()}>
              <Upload className="h-3 w-3" />
            </Button>
            <Button size="icon" variant="destructive" className="h-7 w-7" onClick={onRemoveImage}>
              <Trash2 className="h-3 w-3" />
            </Button>
          </div>
        </div>
      ) : (
        <div
          onClick={() => fileRef.current?.click()}
          className="border-2 border-dashed rounded p-4 text-center cursor-pointer hover:border-primary/50 h-24 flex flex-col items-center justify-center"
        >
          <Upload className="h-5 w-5 text-muted-foreground mb-1" />
          <p className="text-xs text-muted-foreground">{isRTL ? "رفع صورة" : "Upload image"}</p>
          <p className="text-[10px] text-muted-foreground mt-0.5">
            {isRTL ? "1280×720 (16:9) — حتى 5MB" : "1280×720 (16:9) — up to 5MB"}
          </p>
        </div>
      )}

      <div>
        <Label className="text-xs">{isRTL ? "رابط الإعلان" : "Link URL"}</Label>
        <Input
          value={linkUrl}
          onChange={(e) => setLinkUrl(e.target.value)}
          onBlur={() => onUpdate({ link_url: linkUrl })}
          placeholder="https://..."
          className="text-xs h-8"
        />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label className="text-xs">{isRTL ? "بداية" : "Start"}</Label>
          <Input
            type="datetime-local"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            onBlur={() => onUpdate({ start_date: startDate ? new Date(startDate).toISOString() : null })}
            className="text-xs h-8"
          />
        </div>
        <div>
          <Label className="text-xs">{isRTL ? "نهاية" : "End"}</Label>
          <Input
            type="datetime-local"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            onBlur={() => onUpdate({ end_date: endDate ? new Date(endDate).toISOString() : null })}
            className="text-xs h-8"
          />
        </div>
      </div>
    </div>
  );
}